/**
 * Numbers that say what they are, and shapes that answer before the number is
 * read.
 *
 * Two rules live here. A quantity is never rendered bare: it carries its unit
 * as visible text, and the untruncated raw value stays on the element's title
 * and stays selectable, so an operator can still copy the exact figure out of a
 * rounded display. And wherever a count is one of a total, a proportion, or a
 * ranked row, it gets a bar beside it - shape and colour reach the reader
 * before the digits do.
 */

import type { ReactNode } from 'react';
import { cx } from '../utils/cx';
import type { Tone } from './status';

const TONE_FILL: Record<Tone, string> = {
  success: 'bg-success-solid',
  warning: 'bg-warning-solid',
  error: 'bg-error-solid',
  info: 'bg-brand-solid',
  neutral: 'bg-fg-quaternary',
};

/** A duration in seconds, rendered at a sensible scale but never unlabelled. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '—';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
}

/** Thousands separators, so six figures are readable at a glance. */
export function formatCount(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString() : '—';
}

/**
 * A noun agreeing with the count in front of it.
 *
 * `1 requests` and `1 recommendation runs` both shipped, on two different
 * screens, because every call site spelled its own unit as a fixed plural. The
 * rule lives here instead: pass the singular, pass an explicit `plural` only
 * when adding an `s` is wrong.
 */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return Math.abs(count) === 1 ? singular : (pluralForm ?? `${singular}s`);
}

export function formatPercent(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return '—';
  const percent = fraction * 100;
  return `${percent >= 10 || percent === 0 ? percent.toFixed(0) : percent.toFixed(digits)}%`;
}

/**
 * A number with its unit attached. `raw` is the exact value behind a rounded or
 * abbreviated display; it goes on the title so it is one hover away and it is
 * never rounded out of reach.
 */
export function Quantity({
  value,
  unit,
  raw,
  className,
}: {
  /** Already formatted, e.g. `2.3` or `1,204`. */
  value: ReactNode;
  /** The unit as the operator should read it: `s`, `ms`, `requests`, `%`. */
  unit?: string;
  /** The untruncated underlying value, kept reachable on hover. */
  raw?: string | number;
  className?: string;
}) {
  return (
    <span
      className={cx('select-text tabular-nums', className)}
      {...(raw === undefined ? {} : { title: String(raw) })}
    >
      {value}
      {unit ? <span className="ml-0.5 text-[0.85em] font-normal text-tertiary">{unit}</span> : null}
    </span>
  );
}

/**
 * A proportion as a bar. `fraction` is clamped rather than validated, because a
 * count that exceeds its own total is a data oddity the operator should still
 * be able to see the row for.
 */
export function Bar({
  fraction,
  tone = 'info',
  label,
  className,
}: {
  fraction: number;
  tone?: Tone;
  /** Announced to a screen reader, which cannot see the bar. */
  label: string;
  className?: string;
}) {
  const clamped = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  return (
    <span
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cx('block h-1.5 overflow-hidden rounded-full bg-secondary', className)}
    >
      <span
        className={cx('block h-full rounded-full', TONE_FILL[tone], clamped > 0 && 'min-w-px')}
        style={{ width: `${(clamped * 100).toFixed(1)}%` }}
      />
    </span>
  );
}

/**
 * A count, its share of a total, and the bar for that share, as one row. This
 * is the shape a ranked list wants: the eye reads the bars, the operator reads
 * the number only for the row the bars pointed at.
 */
export function CountBar({
  count,
  total,
  unit,
  tone = 'info',
  label,
}: {
  count: number;
  total: number;
  unit?: string;
  tone?: Tone;
  label: string;
}) {
  const fraction = total > 0 ? count / total : 0;
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span className="flex items-baseline justify-between gap-2 text-xs">
        <Quantity value={formatCount(count)} raw={count} {...(unit ? { unit } : {})} />
        <span className="text-tertiary">{total > 0 ? formatPercent(fraction) : '—'}</span>
      </span>
      <Bar
        fraction={fraction}
        tone={tone}
        label={`${label}: ${formatCount(count)} of ${formatCount(total)}`}
      />
    </span>
  );
}

/**
 * A raw identifier an operator genuinely needs, given a human label and kept
 * selectable. Identifiers are never shown as a bare label - the words say what
 * the thing is, the monospace value is the thing itself.
 */
export function IdentifierValue({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-xs text-tertiary">{label}</span>
      <code
        className="block truncate font-mono text-xs break-all select-text text-secondary"
        title={value}
      >
        {value}
      </code>
    </span>
  );
}
